import {User} from "../../../../../api/accessControl/User.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import {Transaction} from "../../TransactionsTable.tsx";
import {BackendApi} from "../../../../../api/api.ts";
import React, {useEffect, useState} from "react";
import NarrowDrawer from "../../../../../components/general/NarrowDrawer.tsx";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import {useSearchParams} from "react-router-dom";
import {capitalize} from "../../../../../lib/TFICLib.ts";
import {Filter, FilterOperation, FilterService} from "./FilterService.ts";
import {BaseTransactionsRepository} from "../../../../../api/baseTransactions/BaseTransactionsRepository.ts";
import {BaseTransaction} from "../../../../../api/baseTransactions/BaseTransaction.ts";
import {TransactionsTable, TransactionsTableViewerRole} from "./TransactionsTable.tsx";
import {PaginationState, SortingState} from "../../../../../components/table/CreateTable.tsx";
import {CustomerBankAccount} from "../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import {CustomerBankAccountRepository} from "../../../../../api/customerBankAccounts/CustomerBankAccountRepository.ts";
import { GlAccount, useGLAccounts } from "../../../../../hooks/useGLAccounts.ts";
import { ComboboxList } from "../../../../../components/general/ComboboxList.tsx";


function renderFilter(filter: Filter, bankAccounts: Array<CustomerBankAccount>) {
    const columnName = capitalize(filter.columnId)

    const label = {
        'Amount': 'Bedrag',
        'BookingDate': 'Datum',
        'Flow': 'Flow',
        'GeneralLedgerAccountId': 'Grootboekrekening',
        'RelationIBAN': 'Relatie IBAN',
        'AccountId': 'Bankrekening',
        'Category': 'Categorie'
    }[columnName]

    const formatValue = (value: string | number) => {
        if (filter.columnId === 'bookingDate') {
            const date = new Date(value)
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('nl-NL', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                })
            }
        }

        if (filter.columnId === 'amount' || filter.columnId === 'value') {
            if (typeof value === 'number') {
                return (value / 100).toLocaleString('nl-NL', {
                    style: 'currency',
                    currency: 'EUR'
                })
            }
        }

        if (filter.columnId === 'accountId') {
            const bankAccount = bankAccounts.find(acc => acc.id === value)
            return bankAccount ? `${bankAccount.ownerName} (${bankAccount.displayIdentifier()})` : value
        }

        if (filter.columnId === 'flow') {
            return value === 'inflow' ? 'inkomend' : 'uitgaand'
        }

        if (filter.columnId === 'category') {
            return value;
        }

        return value
    }

    const firstValue = formatValue(filter.values[0])
    const secondValue = filter.values[1] ? formatValue(filter.values[1]) : null

    if (filter.operation === FilterOperation.BETWEEN) {
        return <span>{label} is tussen {firstValue} en {secondValue}</span>
    }

    if (filter.operation === FilterOperation.EQUALS) {
        return <span>{label} is {firstValue}</span>
    }

    if (filter.operation === FilterOperation.GREATER_THAN) {
        return <span>{label} is meer dan {firstValue}</span>
    }

    if (filter.operation === FilterOperation.LESS_THAN) {
        return <span>{label} is minder dan {firstValue}</span>
    }

    if (filter.operation === FilterOperation.CONTAINS) {
        return <span>{label} bevat {firstValue}</span>
    }
}


async function pullTransactions(
    transactionRepository: BaseTransactionsRepository,
    businessId: string,
    sorting: SortingState,
    filters: Filter[],
) {
    const baseTransactions = await transactionRepository.getBusinessBankTransactions(
        businessId,
        0,
        100000,
        sorting?.orderBy,
        sorting?.orderDescending
    )

    const transactions: Transaction[] = baseTransactions.results.map((original: BaseTransaction) => ({
        id: original.id,
        desc: original.desc,
        generalLedgerAccount: original.generalLedgerAccount,
        bookingDate: original.date,
        amount: original.amount,
        flow: original.flow,
        relationName: original.relationName,
        relationIBAN: original.iban,
        value: original.value,
        accountId: original.accountId,
        reconciliationId: original.reconciliationId,
        vatCode: original.vatCode
    }))

    let finalTransactions: Transaction[] = transactions

    if (filters) {
        const filterService = new FilterService(transactions)
        finalTransactions = filterService.apply(filters)
    }
    return finalTransactions;
}

export function TransactionsPage({currentUser}: { currentUser: User }) {
    const { glAccounts, isLoading: glAccountsLoading,  } = useGLAccounts(currentUser);
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const tablePaginationState = useState<PaginationState<Transaction>>();
    const tableSortingState = useState();

    const [transactions, setTransactions] = useState<Array<Transaction>>([]);
    const [bankAccounts, setBankAccounts] = useState<Array<CustomerBankAccount>>([]);

    const filters = JSON.parse(searchParams.get('filters')) || [];
    const dateFilter = filters.find(f => f.columnId === 'bookingDate');
    const flowFilter = filters.find(f => f.columnId === 'flow');
    const amountFilter = filters.find(f => f.columnId === 'value');

    const [startDate, setStartDate] = useState<string>(dateFilter?.values?.[0] || '');
    const [endDate, setEndDate] = useState<string>(dateFilter?.values?.[1] || '');
    const [flowValue, setFlowValue] = useState<string>(flowFilter?.values?.[0] || 'any');
    const [amountComparatorType, setAmountComparatorType] = useState<string>(
        amountFilter?.operation === FilterOperation.GREATER_THAN ? 'gt' : 'lt'
    );
    const [amountComparisonAmount, setAmountComparisonAmount] = useState<string>(
        amountFilter ? (amountFilter.values[0] / 100).toString() : ''
    );

    const [selectedBankAccount, setSelectedBankAccount] = useState<string>(
        filters.find(f => f.columnId === 'accountId')?.values[0] || ''
    );
    const [selectedRelationIBAN, setSelectedRelationIBAN] = useState<string>(
        filters.find(f => f.columnId === 'relationIBAN')?.values[0] || ''
    );
    const [selectedCategory, setSelectedCategory] = useState<string>(
        filters.find(f => f.columnId === 'category')?.values[0] || ''
    );

    const api = new BackendApi(currentUser.getToken())

    const categoryLabelOptions: string[] = Array.from(
        new Set(
            glAccounts
                .map((account) => account.cashflow_subcategory_nl)
                .filter((label): label is string => !!label)
        )
    ).sort();

    async function onLoad(sorting: SortingState | null = null) {
        setIsLoading(true);
        setTransactions([]);

        const transactionRepository = new BaseTransactionsRepository(api)

        const transactionsFromApi = await pullTransactions(
            transactionRepository,
            currentUser.businesses[0],
            sorting,
            filters,
        )

        setTransactions(transactionsFromApi as Transaction[])

        setIsLoading(false)
    }

    useEffect(() => {
        onLoad()
    }, [searchParams]);

    useEffect(() => {
        new CustomerBankAccountRepository(api).getMyBankAccounts().then(setBankAccounts)
    }, []);

    function updateFilters(newFilters: Array<Filter>) {
        setSearchParams({filters: JSON.stringify(newFilters)});
    }

    function removeFilter(filter: Filter) {
        const newFilters = filters.filter((oldFilter) => oldFilter !== filter);
        updateFilters(newFilters);

        if (filter.columnId === 'bookingDate') {
            setStartDate('');
            setEndDate('');
        } else if (filter.columnId === 'flow') {
            setFlowValue('any');
        } else if (filter.columnId === 'value') {
            setAmountComparisonAmount('');
        } else if (filter.columnId === 'accountId') {
            setSelectedBankAccount('');
        } else if (filter.columnId === 'relationIBAN') {
            setSelectedRelationIBAN('');
        } else if (filter.columnId === 'category') {
            setSelectedCategory('');
        }
    }

    function applyDateFilter(start: string, end: string) {
        const newFilters = filters.filter(f => f.columnId !== 'bookingDate');
        if (start && end) {
            newFilters.push({
                id: 'filterBookingDate',
                operation: FilterOperation.BETWEEN,
                columnId: 'bookingDate',
                values: [start, end]
            });
        }
        updateFilters(newFilters);
    }

    function applyFlowFilter(flow: string) {
        const newFilters = filters.filter(f => f.columnId !== 'flow');
        if (flow && flow !== 'any') {
            newFilters.push({
                id: 'filterFlow',
                operation: FilterOperation.EQUALS,
                columnId: 'flow',
                values: [flow]
            });
        }
        updateFilters(newFilters);
    }

    function applyAmountFilter(comparator: string, amount: string) {
        const newFilters = filters.filter(f => f.columnId !== 'amount');
        if (comparator && amount) {
            newFilters.push({
                id: 'filterAmount',
                operation: comparator === 'gt' ? FilterOperation.GREATER_THAN : FilterOperation.LESS_THAN,
                columnId: 'amount',
                values: [parseFloat(amount) * 100]
            });
        }
        updateFilters(newFilters);
    }

    function applyBankAccountFilter(accountId: string) {
        const newFilters = filters.filter(f => f.columnId !== 'accountId');
        if (accountId) {
            newFilters.push({
                id: 'filterBankAccount',
                operation: FilterOperation.EQUALS,
                columnId: 'accountId',
                values: [accountId]
            });
        }
        updateFilters(newFilters);
    }

    function applyRelationIBANFilter(iban: string) {
        const newFilters = filters.filter(f => f.columnId !== 'relationIBAN');
        if (iban) {
            newFilters.push({
                id: 'filterRelationIBAN',
                operation: FilterOperation.CONTAINS,
                columnId: 'relationIBAN',
                values: [iban]
            });
        }
        updateFilters(newFilters);
    }

    function applyCategoryFilter(category: string) {
        const newFilters = filters.filter(f => f.columnId !== 'category');
        if (category) {
            newFilters.push({
                id: 'filterCategory',
                operation: FilterOperation.EQUALS,
                columnId: 'category',
                values: [category]
            });
        }
        updateFilters(newFilters);
    }

    return (
        <>
            <NarrowDrawer open={isFilterDrawerOpen} updateOpenState={setIsFilterDrawerOpen} title={'Filters'}>
                <div className={'flex flex-col gap-3 mb-8'}>
                    <div className="flex items-center gap-x-2">
                        <span className={'block bg-gray-100 py-0.5 px-2 rounded-md'}>Bankrekening</span> is
                        <select
                            value={selectedBankAccount}
                            onChange={(e) => {
                                setSelectedBankAccount(e.target.value);
                                applyBankAccountFilter(e.target.value);
                            }}
                            className="px-2 focus:outline-none block rounded-md border-0 py-1.5 pr-15 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary sm:text-sm sm:leading-6"
                        >
                            <option value="">Alle rekeningen</option>
                            {bankAccounts.map(account => (
                                <option key={account.id} value={account.id}>
                                    {account.ownerName} ({account.displayIdentifier()})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-x-2">
                        <span className={'block bg-gray-100 py-0.5 px-2 rounded-md'}>Datum</span> is
                        <span className='mr-2'>tussen</span>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => {
                                setStartDate(e.target.value);
                                applyDateFilter(e.target.value, endDate);
                            }}
                            className="flex-1 border-0 bg-transparent py-1 px-2 text-gray-900 border-2 border-gray-200 rounded-md"
                            max={endDate || undefined}
                        />
                        <span className='mx-2'>en</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => {
                                setEndDate(e.target.value);
                                applyDateFilter(startDate, e.target.value);
                            }}
                            className="flex-1 border-0 bg-transparent py-1 px-2 text-gray-900 border-2 border-gray-200 rounded-md"
                            min={startDate || undefined}
                        />
                    </div>
                    <div className="flex items-center gap-x-2">
                        <span className={'block bg-gray-100 py-0.5 px-2 rounded-md'}>Flow</span> is
                        <select
                            value={flowValue}
                            onChange={(e) => {
                                setFlowValue(e.target.value);
                                applyFlowFilter(e.target.value);
                            }}
                            className="px-2 block focus:outline-none rounded-md border-0 py-1.5 pr-15 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary sm:text-sm sm:leading-6"
                        >
                            <option value="any">beide</option>
                            <option value="inflow">inkomend</option>
                            <option value="outflow">uitgaand</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-x-2">
                        <span className={'block bg-gray-100 py-0.5 px-2 rounded-md'}>Bedrag</span> is
                        <select
                            value={amountComparatorType}
                            onChange={(e) => {
                                setAmountComparatorType(e.target.value);
                                applyAmountFilter(e.target.value, amountComparisonAmount);
                            }}
                            className="px-2 focus:outline-none block rounded-md border-0 py-1.5 pr-15 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary sm:text-sm sm:leading-6"
                        >
                            <option value="gt">meer dan</option>
                            <option value="lt">minder dan</option>
                        </select>
                        <input
                            type="number"
                            value={amountComparisonAmount}
                            onChange={(e) => {
                                setAmountComparisonAmount(e.target.value);
                                applyAmountFilter(amountComparatorType, e.target.value);
                            }}
                            className="border-0 bg-transparent py-0.5 px-2 text-gray-900 border-2 border-gray-200 rounded-md"
                            min="0"
                            step="0.01"
                        />
                        euro
                    </div>

                    <div className="flex items-center gap-x-2">
                        <span className={'block bg-gray-100 py-0.5 px-2 rounded-md'}>Relatie IBAN</span> bevat
                        <input
                            type="text"
                            value={selectedRelationIBAN}
                            onChange={(e) => {
                                setSelectedRelationIBAN(e.target.value);
                                applyRelationIBANFilter(e.target.value);
                            }}
                            placeholder="Voer IBAN in"
                            className="flex-1 border-0 bg-transparent py-1 px-2 text-gray-900 border-2 border-gray-200 rounded-md"
                        />
                    </div>
                    <div className="flex items-center gap-x-2">
                        <span className={'block bg-gray-100 py-0.5 px-2 rounded-md'}>Categorie</span> is
                        <ComboboxList
                            label=""
                            disabled={glAccountsLoading}
                            className="block w-full disabled:opacity-75 disabled:text-gray-500 rounded-md bg-white shadow-xs shadow-sm ring-1 ring-inset ring-gray-300 outline-none py-[0.52rem] mb-[0.05rem] pr-12 pl-3 text-base text-gray-900 outline-1 placeholder:text-gray-400 sm:text-sm/6"
                            emptySelectionMessage="Alle categorieën"
                            onSelect={(option) => {
                                const label = option?.id ?? "";
                                setSelectedCategory(label);
                                applyCategoryFilter(label);
                            }}
                            options={categoryLabelOptions as any}
                            selected={selectedCategory || null as any}
                            internalMapper={(item: string) => ({
                                ...(item as any),
                                id: item,
                                displayName: item,
                            })}
                        />
                    </div>
                </div>

                <div className="flex justify-end">
                    <PrimaryButton
                        title={'Toepassen'}
                        onClick={() => setIsFilterDrawerOpen(false)}
                    />
                </div>
            </NarrowDrawer>
            <Shell currentUser={currentUser} title="Transacties">

                <div className="flex flex-col">
                    <div className="flex justify-between">
                        <div>
                            <div className="mr-auto max-w-7xl py-3 sm:flex sm:items-center sm:pr-6 ">
                                <h3 className="text-sm font-medium text-gray-500">
                                    Filters
                                </h3>

                                <div aria-hidden="true" className="hidden h-5 w-px bg-gray-300 sm:ml-4 sm:block"/>

                                <div className="mt-2 sm:ml-4 sm:mt-0">
                                    <div className="-m-1 flex flex-wrap items-center">
                                        {filters.map((activeFilter) => (
                                            <span
                                                className="m-1 inline-flex items-center rounded-full border border-gray-200 bg-white py-1.5 pl-3 pr-2 text-sm font-medium text-gray-900"
                                            >
                                                <span>{renderFilter(activeFilter, bankAccounts)}</span>
                                                <button
                                                    type="button"
                                                    className="ml-1 inline-flex h-4 w-4 flex-shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500"
                                                    onClick={() => removeFilter(activeFilter)}
                                                >
                                                    <span className="sr-only">Remove filter for {activeFilter.label}</span>
                                                    <svg fill="none" stroke="currentColor" viewBox="0 0 8 8" className="h-2 w-2">
                                                        <path d="M1 1l6 6m0-6L1 7" strokeWidth="1.5" strokeLinecap="round"/>
                                                    </svg>
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='flex items-center mr-4'>
                            <PrimaryButton title={'Filter toevoegen'}
                                           onClick={() => setIsFilterDrawerOpen(!isFilterDrawerOpen)} className=""/>
                        </div>
                    </div>
                    <TransactionsTable
                        isLoading={isLoading}
                        transactions={transactions}
                        bankAccounts={bankAccounts}
                        viewerRole={TransactionsTableViewerRole.ENTREPRENEUR}
                        sortingState={tableSortingState}
                        paginationState={tablePaginationState}
                        onQueryUpdate={(pagination, sorting) => onLoad(sorting)}/>
                </div>
            </Shell>
        </>

    )
}